"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PLAN_ID_PATTERN = exports.CommissionPlanSchema = exports.CommissionRuleSchema = exports.ANY_TARGET = exports.COMMISSION_RECURRENCES = exports.COMMISSION_BASES = exports.COMMISSION_SCOPES = void 0;
exports.isPayablePlan = isPayablePlan;
exports.isAssignablePlan = isAssignablePlan;
exports.planNotAssignableMessage = planNotAssignableMessage;
exports.resolvePlanId = resolvePlanId;
exports.findRule = findRule;
exports.targetsOf = targetsOf;
exports.computeRuleCommission = computeRuleCommission;
exports.computePlanCommission = computePlanCommission;
exports.validatePlanRules = validatePlanRules;
exports.ruleStillPays = ruleStillPays;
exports.slugifyPlanId = slugifyPlanId;
exports.uniquePlanId = uniquePlanId;
const zod_1 = require("zod");
/**
 * COMMISSION PLANS — partner commission as a versioned set of rules.
 *
 * Approved design: `mockups/partner-commission-plans.html`.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS REPLACES
 * ---------------------------------------------------------------------------
 *
 * Three loose fields on a deal (`commissionMode` / `flatFee` /
 * `percentageRate`) that paid ONE number, ONCE, at the first subscription
 * conversion. Device sales paid nothing. Package upgrades paid nothing. The
 * financing wizard wrote an `affiliateCommission` onto every device SKU that no
 * service ever read.
 *
 * A plan is a list of RULES. A rule says: for this KIND of thing (a software
 * package, a device offer, the subscription), and this TARGET (a specific id,
 * or `*` for anything not named), pay this RATE on this BASIS, once or monthly.
 *
 * ---------------------------------------------------------------------------
 * THE INVARIANTS THAT MATTER (all enforced below, all tested)
 * ---------------------------------------------------------------------------
 *
 * 1. INTEGER CENTS, ALWAYS FLOORED. A commission is money we owe; rounding up
 *    systematically overpays across thousands of events. Floor, never round.
 * 2. NEVER NEGATIVE. A device sold below cost yields 0 commission, never a
 *    debit against the partner.
 * 3. MARGIN BASIS NEEDS A COST. `percent_of_margin` without a known cost
 *    returns null (unpayable), never 0 and never a guess — silently paying 0
 *    on a missing cost is how partners get quietly underpaid.
 * 4. A RENTAL HAS NO MARGIN AT POINT OF SALE. The device is never sold, so
 *    `percent_of_margin` is refused on a rental target at VALIDATION time
 *    rather than paying 0 at runtime.
 * 5. EXACT TARGET BEATS WILDCARD. Always, regardless of rule order.
 * 6. VERSIONED. Rates change; last quarter's payouts must still explain
 *    themselves, so an event records planVersion + ruleId, never a pointer to
 *    whatever the plan says today.
 */
exports.COMMISSION_SCOPES = [
    'software_package',
    'device_offer',
    'subscription',
    'merchant_processing',
];
/**
 * How a rate turns into money.
 * - `percent_of_price`  — rate% of what the customer paid (gross).
 * - `percent_of_margin` — rate% of (gross - cost). Devices only; needs a cost.
 * - `flat_per_unit`     — a fixed number of cents, ignoring rate arithmetic.
 */
exports.COMMISSION_BASES = ['percent_of_price', 'percent_of_margin', 'flat_per_unit'];
/** `once` pays a single time; `monthly` pays every billing cycle. */
exports.COMMISSION_RECURRENCES = ['once', 'monthly'];
/** The wildcard target — "anything in this scope that no other rule names". */
exports.ANY_TARGET = '*';
exports.CommissionRuleSchema = zod_1.z.object({
    /** Stable within the plan. Written onto every event this rule pays, forever. */
    ruleId: zod_1.z.string().min(1),
    scope: zod_1.z.enum(exports.COMMISSION_SCOPES),
    /** A packageId / skuId / modelId / bundleId, or `*`. */
    target: zod_1.z.string().min(1).default(exports.ANY_TARGET),
    /**
     * ADDITIONAL targets this same rule also covers (2026-08-27).
     *
     * One rate frequently applies to a SET of things — "15% on all three
     * compliance packages" — and expressing that as three near-identical rules
     * makes a rate change three edits, one of which will eventually be missed.
     *
     * Additive: `target` remains the primary and every rule written before this
     * field behaves identically. `targetsOf` below is the ONE place the two are
     * combined; never read `target` alone when matching.
     */
    targets: zod_1.z.array(zod_1.z.string().min(1)).optional(),
    basis: zod_1.z.enum(exports.COMMISSION_BASES),
    /**
     * For percent bases: a PERCENTAGE (20 = 20%), matching the existing
     * `percentageRate` convention rather than a 0-1 fraction.
     * For `flat_per_unit`: ignored — `flatCents` carries the amount.
     */
    rate: zod_1.z.number().min(0).max(100).default(0),
    /** For `flat_per_unit`: the amount in integer cents. */
    flatCents: zod_1.z.number().int().min(0).default(0),
    recurrence: zod_1.z.enum(exports.COMMISSION_RECURRENCES).default('once'),
    /**
     * For `monthly`: how many cycles it pays. Absent/null = for the life of the
     * subscription. Meaningless for `once`.
     */
    months: zod_1.z.number().int().positive().nullable().optional(),
    /** Optional ceiling on a single event's commission, integer cents. */
    capCents: zod_1.z.number().int().min(0).optional(),
    /**
     * Optional floor, integer cents — "at least $X per sale". Applied only when
     * the rule pays anything at all, so a zero-margin sale still pays 0 rather
     * than being lifted to the floor.
     */
    floorCents: zod_1.z.number().int().min(0).optional(),
    /** Free text shown to operators, never used in arithmetic. */
    note: zod_1.z.string().optional(),
});
exports.CommissionPlanSchema = zod_1.z.object({
    planId: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1),
    description: zod_1.z.string().optional(),
    status: zod_1.z.enum(['draft', 'active', 'retired']).default('draft'),
    /**
     * Bumped on every rate-affecting edit. Stamped onto each event so a payout
     * made under v3 still explains itself after the plan reaches v7.
     */
    version: zod_1.z.number().int().positive().default(1),
    /** Exactly one plan may be the global fallback. Enforced in-service. */
    isDefault: zod_1.z.boolean().default(false),
    rules: zod_1.z.array(exports.CommissionRuleSchema).default([]),
    createdAt: zod_1.z.number(),
    updatedAt: zod_1.z.number(),
    createdBy: zod_1.z.string().optional(),
    updatedBy: zod_1.z.string().optional(),
});
// ---------------------------------------------------------------------------
// PUBLICATION — what a plan must be before it may price real money
// ---------------------------------------------------------------------------
/**
 * THE GATE (2026-09-03). A plan prices real commissions only when it is
 * `active`.
 *
 * WHY THIS EXISTS. `status` was stored from the beginning and enforced
 * NOWHERE: nothing checked it when a plan was made the global fallback,
 * nothing checked it when a deal or a partner link pinned one, and `pickPlan`
 * resolved purely by id. A plan named "temp plan", left in `draft`, was found
 * holding `isDefault: true` — the platform-wide fallback pricing every
 * commission on the system, at rates nobody had finished writing.
 *
 * A DRAFT IS AN UNFINISHED THOUGHT. Its rates are half-entered by definition;
 * that is what draft MEANS. Paying against one is not a smaller mistake than
 * paying against no plan at all — it is worse, because it looks deliberate.
 *
 * `retired` is deliberately NOT blocked from PAYING. Retiring a plan means
 * "stop putting this on new things", not "stop honouring what it already
 * sold" — a partner mid-term under a retired plan must keep earning, and
 * every event carries its own `planVersion` so an old payout still explains
 * itself. Retirement IS blocked from new ASSIGNMENTS, which is the check
 * callers make with `isAssignablePlan`.
 *
 * ONE RULE, FOUR ENFORCEMENT POINTS, all pointing here:
 *   1. billing-service `upsertPlan` — `isDefault` forces `active`.
 *   2. super-admin-ext `deals` — a deal may not pin a non-assignable plan.
 *   3. affiliate-service `dealLinks` — nor may a partner link.
 *   4. billing-service `pickPlan` — the runtime backstop: a draft plan is
 *      invisible to the cascade, so even a plan that slipped past 1-3 (or was
 *      written before they existed) can never price a payout.
 *
 * affiliate-service does NOT depend on this package — it inlines the same
 * check. IF YOU CHANGE THIS, CHANGE THAT COPY TOO (`handlers/dealLinks.ts`).
 */
function isPayablePlan(plan) {
    return plan?.status === 'active' || plan?.status === 'retired';
}
/**
 * May this plan be newly PINNED to a deal, a partner link or an offer?
 *
 * Stricter than `isPayablePlan`: only `active`. A draft is unfinished and a
 * retired plan is one an operator has deliberately taken out of circulation —
 * neither belongs on something being set up today, even though a retired one
 * keeps paying what it already sold.
 */
function isAssignablePlan(plan) {
    return plan?.status === 'active';
}
/** The refusal an operator reads. Same words from every enforcement point. */
function planNotAssignableMessage(plan, planId) {
    if (!plan)
        return `No such commission plan: ${planId}.`;
    const named = plan.name ? `"${plan.name}"` : planId;
    if (plan.status === 'draft') {
        return `Commission plan ${named} is still a DRAFT and cannot be assigned — a draft's rates are unfinished, and assigning one would price real payouts against them. Set it to Active on the plan first.`;
    }
    return `Commission plan ${named} is RETIRED and cannot be assigned to anything new. It keeps paying what it already sold; pick a live plan for this.`;
}
// ---------------------------------------------------------------------------
// RESOLUTION
// ---------------------------------------------------------------------------
/**
 * Which plan applies. Offer override -> partner pin -> deal's plan -> global
 * default. Deliberately the same cascade shape as `resolveCommissionTerms`,
 * with a partner tier added.
 *
 * Returns null only when NOTHING is configured, which callers must treat as
 * "pay nothing and log", never as "pay a default rate".
 */
function resolvePlanId(offerPlanId, partnerPlanId, dealPlanId, globalDefaultPlanId) {
    return offerPlanId || partnerPlanId || dealPlanId || globalDefaultPlanId || null;
}
/**
 * The rule that applies to one thing being sold.
 *
 * An EXACT target match always wins over the wildcard, whatever order the rules
 * are stored in — relying on array order would make payouts depend on how an
 * operator happened to drag rows around in the editor.
 *
 * Among several exact matches for the same target (a data defect — the editor
 * should prevent it) the FIRST is taken deterministically rather than the
 * highest or the last, so a replay of the same event always pays the same.
 */
function findRule(plan, scope, targetId) {
    const rules = plan?.rules ?? [];
    const inScope = rules.filter((r) => r.scope === scope);
    if (inScope.length === 0)
        return null;
    if (targetId) {
        // Primary target OR any of the extra targets — see `targetsOf`.
        const exact = inScope.find((r) => targetsOf(r).includes(targetId));
        if (exact)
            return exact;
    }
    return inScope.find((r) => r.target === exports.ANY_TARGET) ?? null;
}
/**
 * Every id a rule covers: its primary `target` plus any extras, de-duplicated.
 * The ONE place the two fields are combined — matching and duplicate detection
 * both go through it, so they can never disagree about what a rule covers.
 */
function targetsOf(rule) {
    const out = [rule.target, ...(rule.targets ?? [])].filter(Boolean);
    return Array.from(new Set(out));
}
/** Floors to whole cents and clamps at zero — commission is never a debit. */
const floorCents = (v) => {
    if (!Number.isFinite(v) || v <= 0)
        return 0;
    return Math.floor(v);
};
/**
 * What ONE event pays under ONE rule.
 *
 * `cap` is applied before `floor` so that a floor cannot lift a payment back
 * above its cap — a rule carrying both must still honour the ceiling.
 */
function computeRuleCommission(rule, input) {
    if (!rule)
        return { commissionCents: null, reason: 'no_rule' };
    const gross = Number.isFinite(input?.grossCents) ? Math.trunc(input.grossCents) : 0;
    if (rule.basis === 'flat_per_unit') {
        // A flat rule pays its amount even on a zero-value line (a comped device
        // still cost the partner the same work), so it does NOT check the base.
        let amount = floorCents(rule.flatCents ?? 0);
        if (typeof rule.capCents === 'number')
            amount = Math.min(amount, rule.capCents);
        return { commissionCents: amount, reason: 'ok', basisApplied: rule.basis, rateApplied: rule.flatCents };
    }
    let base;
    if (rule.basis === 'percent_of_margin') {
        if (typeof input?.costCents !== 'number' || !Number.isFinite(input.costCents)) {
            // Unknown cost. NOT zero — a margin rule with no cost is unpayable and
            // must be surfaced, not silently paid as nothing.
            return { commissionCents: null, reason: 'missing_cost', basisApplied: rule.basis };
        }
        base = gross - Math.trunc(input.costCents);
    }
    else {
        base = gross;
    }
    if (base <= 0) {
        // Sold at or below cost: nothing to share. A real, payable answer of zero
        // — distinct from `missing_cost`, which is an unanswerable question.
        return { commissionCents: 0, reason: 'non_positive_base', basisApplied: rule.basis, rateApplied: rule.rate };
    }
    let amount = floorCents((base * (rule.rate ?? 0)) / 100);
    if (typeof rule.capCents === 'number')
        amount = Math.min(amount, rule.capCents);
    if (typeof rule.floorCents === 'number' && amount > 0)
        amount = Math.max(amount, rule.floorCents);
    if (typeof rule.capCents === 'number')
        amount = Math.min(amount, rule.capCents);
    return { commissionCents: amount, reason: 'ok', basisApplied: rule.basis, rateApplied: rule.rate };
}
/** Convenience: resolve the rule and compute in one call. */
function computePlanCommission(plan, scope, targetId, input) {
    return computeRuleCommission(findRule(plan, scope, targetId), input);
}
/**
 * Editor-time checks the schema alone cannot express.
 *
 * `rentalTargets` is the set of targets that are RENTAL offers. A rental device
 * is never sold, so it has no margin at point of sale — a `percent_of_margin`
 * rule against one would compute against a cost that never converts to
 * revenue. Refused here rather than paying a wrong number forever.
 */
function validatePlanRules(rules, rentalTargets = new Set()) {
    const problems = [];
    const seen = new Map();
    for (const rule of rules ?? []) {
        // Every id the rule covers is claimed, not just its primary target —
        // otherwise two rules could silently both claim the same package through
        // their `targets` lists and only the first would ever pay.
        for (const t of targetsOf(rule)) {
            const key = `${rule.scope}::${t}`;
            seen.set(key, (seen.get(key) ?? 0) + 1);
        }
        if (rule.basis === 'percent_of_margin') {
            if (rule.scope !== 'device_offer') {
                problems.push({ ruleId: rule.ruleId, message: 'Margin basis applies to device offers only — a package or subscription has no unit cost to measure margin against.' });
            }
            else if (targetsOf(rule).some((t) => rentalTargets.has(t))) {
                problems.push({ ruleId: rule.ruleId, message: 'A rental is never sold, so it has no margin at point of sale. Use a flat amount per unit, or a percentage of the monthly rent.' });
            }
        }
        if (rule.basis === 'flat_per_unit' && (rule.flatCents ?? 0) <= 0) {
            problems.push({ ruleId: rule.ruleId, message: 'A flat rule pays nothing until an amount is set.' });
        }
        if (rule.basis !== 'flat_per_unit' && (rule.rate ?? 0) <= 0) {
            problems.push({ ruleId: rule.ruleId, message: 'A percentage rule pays nothing until a rate is set.' });
        }
        if (rule.recurrence === 'once' && rule.months != null) {
            problems.push({ ruleId: rule.ruleId, message: 'A one-time rule cannot carry a month count.' });
        }
        if (typeof rule.capCents === 'number' && typeof rule.floorCents === 'number' && rule.floorCents > rule.capCents) {
            problems.push({ ruleId: rule.ruleId, message: 'Floor is above cap — this rule can never pay its floor.' });
        }
    }
    for (const [key, count] of seen) {
        if (count > 1) {
            const [scope, target] = key.split('::');
            problems.push({ ruleId: key, message: `Two rules both target ${target} in ${scope} — only the first would ever pay.` });
        }
    }
    return problems;
}
/**
 * Has a `monthly` rule finished paying?
 *
 * `cyclesPaid` counts events ALREADY written for this rule on this
 * subscription. A null/absent `months` runs for life. Compared with `>=` so a
 * 12-month rule pays cycles 0..11 and stops — paying a thirteenth is the
 * classic off-by-one that overpays every partner on every deal.
 */
function ruleStillPays(rule, cyclesPaid) {
    if (rule.recurrence === 'once')
        return cyclesPaid < 1;
    if (rule.months == null)
        return true;
    return cyclesPaid < rule.months;
}
// ---------------------------------------------------------------------------
// PLAN IDS — readable, and permanent
// ---------------------------------------------------------------------------
/**
 * What a minted plan id may contain. Lowercase, digits and single hyphens.
 *
 * A plan id is written onto EVERY commission event it prices and is the key an
 * operator pastes into a payment investigation, so it is also the thing they
 * have to read out loud, paste into a spreadsheet and recognise in a ledger
 * three years later. `cplan_m9x2k4_7f3a1c0d` fails all three.
 */
exports.PLAN_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MAX_PLAN_ID_LENGTH = 60;
/**
 * "Gold Partner — Tier 1" -> "gold-partner-tier-1".
 *
 * Deliberately lossy and deliberately NOT reversible: this produces a
 * CANDIDATE id, which the service then makes unique. Returns '' when a name
 * has nothing usable in it (an emoji-only name, say), and the caller falls
 * back rather than minting an empty key.
 */
function slugifyPlanId(name) {
    return String(name || '')
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, MAX_PLAN_ID_LENGTH)
        // A trailing hyphen can reappear after the slice.
        .replace(/-+$/g, '');
}
/**
 * A readable id that is not already taken.
 *
 * COLLISION IS SUFFIXED, NEVER RESOLVED BY OVERWRITING. The write that stores a
 * plan is a Put keyed on this id, so handing back an id that already exists
 * would silently replace a plan that live commission events already point at —
 * the exact failure the random suffix was originally added to prevent. The
 * suffix is a counter rather than randomness so the second "Gold Partner" plan
 * is `gold-partner-2` and reads like what it is.
 */
function uniquePlanId(name, taken, fallback = 'plan') {
    const base = slugifyPlanId(name) || fallback;
    if (!taken.has(base))
        return base;
    for (let n = 2; n < 1000; n += 1) {
        const candidate = `${base.slice(0, MAX_PLAN_ID_LENGTH - 5)}-${n}`;
        if (!taken.has(candidate))
            return candidate;
    }
    // A thousand plans sharing one name is not a real scenario; refusing beats
    // looping forever or reusing an id.
    throw new Error(`could not mint a unique plan id from "${name}"`);
}
//# sourceMappingURL=commissionPlan.js.map